// Rollup-tree builder (Phase 3a finish) — PURE, renderer-agnostic.
//
// Turns the leaf-grain rows /breakdown returns (at the DEEPEST active dim's grain)
// into a nested rollup over the ACTIVE dimensions (canonical account→campaign→
// adset→ad order, restricted to the active ones), then flattens depth-first into
// display rows: one SUBTOTAL row per level with its children beneath it, exactly
// as the approved `flat_pivot_with_subtotal_rows` mockup shows.
//
// ROLLUP MATH: additive metrics (spend, meta_lead_spend, impressions, clicks,
// link_clicks, engagement, total/meta/website leads, landing_page_views) SUM;
// ratios (cost/lead, cost/meta-lead, CTR, CPC, CPM) are RECOMPUTED from the summed
// numerator/denominator at EACH node — never averaged. Money + money ratios are
// kept PER-CURRENCY (never summed across currencies); pct ratios are single
// numbers. Every ancestor accumulates each leaf row, so a parent row always
// equals the sum of the children shown beneath it (the drill-down invariant).
//
// CONSEQUENCE (by design, not a bug): the source grain deepens as you add
// dimensions, so adding the Ad dimension can slightly reduce coarser totals —
// Meta leaves some campaign spend unattributed to any ad. Parent-equals-sum-of-
// shown-children is the invariant we guarantee; per-level authoritative totals
// would be a future GROUPING SETS change (out of scope).
//
// Renderer-agnostic: returns plain display rows (no JSX) so the Tree view (3b)
// reuses this exact builder, collapsible.

import type {
  CurrencyAmount,
  ReportingBreakdownRow,
  ReportingCreative,
  ReportingDimension,
} from '../../types/reporting';
import {
  dimId,
  dimName,
  statSortValue,
  type BreakdownStatKey,
} from './breakdownColumns';

const r2 = (n: number): number => Math.round(n * 100) / 100;

// Per-node rolled metrics. Additive counts are single numbers; money (spend,
// metaLeadSpend) + money ratios are PER-CURRENCY; pct ratios are currency-agnostic.
export interface NodeMetrics {
  impressions: number;
  clicks: number;
  linkClicks: number;
  engagement: number;
  totalLeads: number;
  metaLeads: number;
  websiteLeads: number;
  landingPageViews: number;
  spend: CurrencyAmount[];
  metaLeadSpend: CurrencyAmount[];
  cpl: CurrencyAmount[]; // Σspend / ΣtotalLeads
  costPerMetaLead: CurrencyAmount[]; // Σ(meta-form spend) / ΣmetaLeads
  costPerLinkClick: CurrencyAmount[]; // Σspend / ΣlinkClicks
  cpcAll: CurrencyAmount[]; // Σspend / Σclicks
  cpm: CurrencyAmount[]; // Σspend / Σimpressions * 1000
  linkCtr: number | null; // ΣlinkClicks / Σimpressions * 100
  ctrAll: number | null; // Σclicks / Σimpressions * 100
  currencies: string[];
}

export type SeparatorTier = 'none' | 'strong' | 'hairline';

// The row's entity ATTRIBUTES (latest-value, non-metric) — present on rows fetched
// AT a single entity's grain (Flat leaves + every Tree node); null on Flat rollup
// subtotals (which aggregate several entities → no single attribute). The renderer
// shows each attribute only on its NATIVE level (objective on the campaign level,
// budget on the budgetLevel-owning level), so a carried value on a non-native row
// is simply never rendered. NOTE: delivery STATUS is NOT here — it's a per-level
// trio on BreakdownRowView (below), populated on EVERY node (subtotals included)
// so each grouped level's name cell shows its own dot.
export interface RowAttributes {
  budgetAmount: number | null;
  budgetType: string | null; // daily | lifetime
  budgetLevel: string | null; // campaign (CBO) | adset (ABO)
  objective: string | null;
  optimizationGoal: string | null;
  currency: string; // the row's native currency (for per-currency budget formatting)
}

// The minimal shape a cell renderer / sort needs — SHARED by the Flat pivot
// (RollupDisplayRow) and the Tree view (each fetched node). Factoring this is what
// lets one set of cell renderers + sort serve both surfaces (they differ only in
// how rows are fetched + the surrounding chrome).
export interface BreakdownRowView {
  dim: ReportingDimension;
  id: string | null; // the node's dim id (null for an unattributed account)
  name: string | null; // display label
  adAccountId: string | null; // act_… for the Meta deep link (campaign/adset/ad)
  metrics: NodeMetrics;
  attributes: RowAttributes | null; // null on rollup subtotals (no single entity)
  // Per-grouped-level delivery status — populated on EVERY node (leaf + subtotal +
  // tree) from the row's per-level fields, so a subtotal row (whose `attributes`
  // is null) still shows its own level's dot. nameStatus(row, dim) reads these.
  campaignStatus: string | null;
  adsetStatus: string | null;
  adStatus: string | null;
  // Ad creative — non-null ONLY on an AD-level leaf node (Flat ad leaf + Tree ad
  // node); null on every coarser node + rollup subtotal (they aggregate many ads,
  // so no single creative applies). Drives the inline thumbnail + enlarge.
  creative: ReportingCreative | null;
}

// Extract a row's attribute block (single-entity grain). Used by the Flat leaf
// nodes + every Tree node.
export function rowAttributes(row: ReportingBreakdownRow): RowAttributes {
  return {
    budgetAmount: row.budgetAmount,
    budgetType: row.budgetType,
    budgetLevel: row.budgetLevel,
    objective: row.objective,
    optimizationGoal: row.optimizationGoal,
    currency: row.currency,
  };
}

// One flattened Flat-view row = one tree node. `dim`/`id`/`name` are the node's
// own level; the renderer puts `name` in the node's dim column and blanks others.
export interface RollupDisplayRow extends BreakdownRowView {
  level: number; // 0 = shallowest active dim
  isSubtotal: boolean; // has children (a rollup row) vs a leaf
  isTopLevel: boolean; // level === 0
  separator: SeparatorTier;
}

export type RollupSortKey = 'name' | BreakdownStatKey;
export interface RollupSort {
  key: RollupSortKey;
  dir: 'asc' | 'desc';
}
// Matches the retired campaign table's default.
export const DEFAULT_ROLLUP_SORT: RollupSort = { key: 'spend', dir: 'desc' };

// ---- internal accumulation --------------------------------------------------

interface CurAcc {
  spend: number;
  metaLeadSpend: number;
  impressions: number;
  clicks: number;
  linkClicks: number;
  engagement: number;
  totalLeads: number;
  metaLeads: number;
  websiteLeads: number;
  landingPageViews: number;
}

function emptyCur(): CurAcc {
  return {
    spend: 0,
    metaLeadSpend: 0,
    impressions: 0,
    clicks: 0,
    linkClicks: 0,
    engagement: 0,
    totalLeads: 0,
    metaLeads: 0,
    websiteLeads: 0,
    landingPageViews: 0,
  };
}

function addRow(cur: CurAcc, row: ReportingBreakdownRow): void {
  cur.spend += row.spend;
  cur.metaLeadSpend += row.metaLeadSpend;
  cur.impressions += row.impressions;
  cur.clicks += row.clicks;
  cur.linkClicks += row.linkClicks;
  cur.engagement += row.engagement;
  cur.totalLeads += row.totalLeads;
  cur.metaLeads += row.metaLeads;
  cur.websiteLeads += row.websiteLeads;
  cur.landingPageViews += row.landingPageViews;
}

interface BuildNode {
  level: number;
  dim: ReportingDimension;
  id: string | null;
  name: string | null;
  adAccountId: string | null;
  // A representative source-grain row for this node's attributes. On a LEAF node
  // this is the node's own single entity → correct attributes; on a subtotal node
  // it's a deep leaf's row, but subtotals blank their attributes on emit anyway.
  // null only on the virtual root (never emitted).
  sampleRow: ReportingBreakdownRow | null;
  byCurrency: Map<string, CurAcc>;
  children: Map<string, BuildNode>;
}

// Roll the per-currency accumulators into the final NodeMetrics (ratios recomputed
// from the summed numerator/denominator, money per-currency, counts summed).
function finalize(byCurrency: Map<string, CurAcc>): NodeMetrics {
  const currencies = [...byCurrency.keys()].sort();
  const accs = currencies.map((c) => byCurrency.get(c)!);
  const sumOf = (f: (a: CurAcc) => number): number => accs.reduce((n, a) => n + f(a), 0);

  const impressions = sumOf((a) => a.impressions);
  const clicks = sumOf((a) => a.clicks);
  const linkClicks = sumOf((a) => a.linkClicks);
  const engagement = sumOf((a) => a.engagement);
  const totalLeads = sumOf((a) => a.totalLeads);
  const metaLeads = sumOf((a) => a.metaLeads);
  const websiteLeads = sumOf((a) => a.websiteLeads);
  const landingPageViews = sumOf((a) => a.landingPageViews);

  const money = (pick: (a: CurAcc) => number): CurrencyAmount[] =>
    currencies.map((currency) => ({ currency, amount: r2(pick(byCurrency.get(currency)!)) }));
  // A money ratio per currency, only where the denominator > 0 (else "—").
  const ratio = (num: (a: CurAcc) => number, den: (a: CurAcc) => number): CurrencyAmount[] =>
    currencies
      .map((currency) => ({ currency, a: byCurrency.get(currency)! }))
      .filter(({ a }) => den(a) > 0)
      .map(({ currency, a }) => ({ currency, amount: r2(num(a) / den(a)) }));

  const pct = (num: number, den: number): number | null => (den > 0 ? r2((num / den) * 100) : null);

  return {
    impressions,
    clicks,
    linkClicks,
    engagement,
    totalLeads,
    metaLeads,
    websiteLeads,
    landingPageViews,
    spend: money((a) => a.spend),
    metaLeadSpend: money((a) => a.metaLeadSpend),
    cpl: ratio((a) => a.spend, (a) => a.totalLeads),
    costPerMetaLead: ratio((a) => a.metaLeadSpend, (a) => a.metaLeads),
    costPerLinkClick: ratio((a) => a.spend, (a) => a.linkClicks),
    cpcAll: ratio((a) => a.spend, (a) => a.clicks),
    cpm: ratio((a) => a.spend * 1000, (a) => a.impressions),
    linkCtr: pct(linkClicks, impressions),
    ctrAll: pct(clicks, impressions),
    currencies,
  };
}

interface FinalNode {
  node: BuildNode;
  metrics: NodeMetrics;
}

function nodeSortValue(fn: FinalNode, sort: RollupSort): number | string | null {
  if (sort.key === 'name') return fn.node.name;
  return statSortValue(fn.metrics, sort.key);
}

function compareNullLast(
  a: number | string | null,
  b: number | string | null,
  factor: number,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // nulls always last, independent of dir
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * factor;
  return String(a).localeCompare(String(b)) * factor;
}

// Order a set of sibling nodes by the active sort (finalizing each once), tiebreak
// on name asc then id — deterministic + hierarchy-preserving (only siblings move).
function sortSiblings(nodes: BuildNode[], sort: RollupSort): FinalNode[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  return nodes
    .map((node) => ({ node, metrics: finalize(node.byCurrency) }))
    .sort((x, y) => {
      const c = compareNullLast(nodeSortValue(x, sort), nodeSortValue(y, sort), factor);
      if (c !== 0) return c;
      const t = (x.node.name ?? '').localeCompare(y.node.name ?? '');
      return t !== 0 ? t : (x.node.id ?? '').localeCompare(y.node.id ?? '');
    });
}

// ---- shared (Flat + Tree) --------------------------------------------------

// Roll a flat set of leaf rows into ONE NodeMetrics (per-currency money, ratios
// recomputed from the summed numerator/denominator — never averaged). Used by the
// Tree view: each fetched node is one authoritative aggregate at its own grain, so
// its metrics come straight from its scoped fetch (single row per currency).
export function accumulateMetrics(rows: readonly ReportingBreakdownRow[]): NodeMetrics {
  const byCurrency = new Map<string, CurAcc>();
  for (const row of rows) {
    const cur = byCurrency.get(row.currency) ?? emptyCur();
    addRow(cur, row);
    byCurrency.set(row.currency, cur);
  }
  return finalize(byCurrency);
}

// The comparable value for sorting a row view by the active sort (name → the label;
// a stat → its summed value). Shared so Flat + Tree order siblings identically.
export function rowViewSortValue(view: BreakdownRowView, sort: RollupSort): number | string | null {
  if (sort.key === 'name') return view.name;
  return statSortValue(view.metrics, sort.key);
}

// Sort a set of sibling row views (hierarchy-preserving — the caller only ever
// passes ONE level's siblings). Same null-last + name/id tiebreak as the Flat
// rollup, so a node's children order exactly as the flat rows would.
export function sortRowViews<T extends BreakdownRowView>(views: readonly T[], sort: RollupSort): T[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...views].sort((a, b) => {
    const c = compareNullLast(rowViewSortValue(a, sort), rowViewSortValue(b, sort), factor);
    if (c !== 0) return c;
    const t = (a.name ?? '').localeCompare(b.name ?? '');
    return t !== 0 ? t : (a.id ?? '').localeCompare(b.id ?? '');
  });
}

// Depth-first pre-order emit: a node's subtotal row first, then its (sorted)
// children beneath it.
function emit(
  nodes: BuildNode[],
  sort: RollupSort,
  out: Array<Omit<RollupDisplayRow, 'separator'>>,
): void {
  for (const { node, metrics } of sortSiblings(nodes, sort)) {
    const isSubtotal = node.children.size > 0;
    out.push({
      level: node.level,
      dim: node.dim,
      id: node.id,
      name: node.name,
      adAccountId: node.adAccountId,
      metrics,
      // Attributes (budget/objective) belong to a single entity: a LEAF node is
      // one source-grain entity → carry its attributes; a subtotal aggregates many
      // → blank.
      attributes: isSubtotal || !node.sampleRow ? null : rowAttributes(node.sampleRow),
      // Per-level status rides EVERY node (subtotals too): a subtotal represents
      // exactly one entity at its level, and its sampleRow (a deeper leaf) carries
      // that level's status via the query's ancestor join. So each grouped level's
      // name cell — subtotal or leaf — shows its own dot.
      campaignStatus: node.sampleRow?.campaignStatus ?? null,
      adsetStatus: node.sampleRow?.adsetStatus ?? null,
      adStatus: node.sampleRow?.adStatus ?? null,
      // Creative rides an AD leaf only. Gate on dim==='ad' (always a leaf, never a
      // subtotal) so a coarser subtotal — whose sampleRow is a deep ad leaf that
      // DOES carry a creative — never inherits some child ad's creative.
      creative: node.dim === 'ad' ? node.sampleRow?.creative ?? null : null,
      isSubtotal,
      isTopLevel: node.level === 0,
    });
    if (isSubtotal) emit([...node.children.values()], sort, out);
  }
}

// Build the rollup + flatten to display rows. `activeDims` must be canonical
// (account→campaign→adset→ad, active ones only); leaf rows are the /breakdown rows
// at that deepest grain.
export function buildRollup(
  leafRows: readonly ReportingBreakdownRow[],
  activeDims: readonly ReportingDimension[],
  sort: RollupSort = DEFAULT_ROLLUP_SORT,
): RollupDisplayRow[] {
  const root: BuildNode = {
    level: -1,
    dim: 'account',
    id: null,
    name: null,
    adAccountId: null,
    sampleRow: null,
    byCurrency: new Map(),
    children: new Map(),
  };

  for (const row of leafRows) {
    let parent = root;
    for (let lvl = 0; lvl < activeDims.length; lvl++) {
      const dim = activeDims[lvl]!;
      const id = dimId(row, dim);
      const key = id ?? '∅'; // ∅ — the null/unattributed bucket
      let node = parent.children.get(key);
      if (!node) {
        node = {
          level: lvl,
          dim,
          id,
          name: dimName(row, dim),
          adAccountId: row.adAccountId,
          sampleRow: row,
          byCurrency: new Map(),
          children: new Map(),
        };
        parent.children.set(key, node);
      }
      if (!node.adAccountId && row.adAccountId) node.adAccountId = row.adAccountId;
      const cur = node.byCurrency.get(row.currency) ?? emptyCur();
      addRow(cur, row);
      node.byCurrency.set(row.currency, cur);
      parent = node;
    }
  }

  const bare: Array<Omit<RollupDisplayRow, 'separator'>> = [];
  emit([...root.children.values()], sort, bare);
  // Separator tier: first row none; a new top-level (level 0) row a strong break;
  // everything deeper a hairline within its group.
  return bare.map((r, i) => ({
    ...r,
    separator: i === 0 ? 'none' : r.isTopLevel ? 'strong' : 'hairline',
  }));
}
